import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { loadDiscoveryCities } from "@/lib/discovery";

export default function TrendingPropertiesSection({ activeCity, setActiveCity }) {
  const [cities, setCities] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    loadDiscoveryCities({ refresh: reloadKey > 0 })
      .then((items) => {
        if (!live) return;
        setCities(items);
        if (!activeCity && items.length > 0) {
          const firstAvailable = items.find((item) => Number(item.property_count) > 0) || items[0];
          setActiveCity(firstAvailable.query_value || firstAvailable.name);
        }
      })
      .catch((requestError) => { if (live) setError(formatApiError(requestError)); });
    return () => { live = false; };
  }, [activeCity, reloadKey, setActiveCity]);

  useEffect(() => {
    if (!activeCity) {
      setLoading(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setError(null);
    // Request limit = 8 to match desktop layout
    api.get("/properties", { params: { city: activeCity, limit: 8, sort: "most_viewed" } })
      .then(({ data }) => {
        if (live) {
          setProperties(data?.items || []);
        }
      })
      .catch((err) => {
        if (live) {
          setError(formatApiError(err));
          setProperties([]);
        }
      })
      .finally(() => {
        if (live) {
          setLoading(false);
        }
      });

    return () => { live = false; };
  }, [activeCity, reloadKey]);

  return (
    <section className="py-16 border-b border-slate-200/40" data-testid="trending-plots-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Top Header & Filters Row */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div className="gold-divider border-l-4 pl-4">
            <h2 className="font-display text-2xl font-black tracking-tight text-slate-900">
              Trending Properties & Plots
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Popular properties and plots buyers are exploring in your selected city.
            </p>
          </div>

          {/* Dynamic City Tabs */}
          {/* Desktop/Tablet Tab Bar */}
          <div className="hidden sm:flex items-center gap-1.5 bg-card border border-border/80 p-1 rounded-xl w-fit overflow-x-auto scrollbar-none relative">
            {cities.map(city => {
              const cityQuery = city.query_value || city.name;
              const isActive = activeCity === cityQuery;
              return (
                <button
                  key={city.slug}
                  type="button"
                  onClick={() => setActiveCity(cityQuery)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 relative z-10 ${
                    isActive
                      ? "text-white"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`trending-city-tab-${city.slug}`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeCityTab"
                      className="absolute inset-0 bg-primary rounded-lg z-[-1] shadow-sm"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  {city.name}
                </button>
              );
            })}
          </div>

          {/* Mobile Chip Bar (Horizontal scrollable chips) */}
          <div className="flex sm:hidden items-center gap-2 overflow-x-auto scrollbar-none pb-1 -mx-6 px-6 w-[100vw] relative">
            {cities.map(city => {
              const cityQuery = city.query_value || city.name;
              const isActive = activeCity === cityQuery;
              return (
                <button
                  key={city.slug}
                  type="button"
                  onClick={() => setActiveCity(cityQuery)}
                  className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap border transition-all duration-200 relative ${
                    isActive
                      ? "border-amber-600 text-amber-700 shadow-sm bg-transparent"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`trending-city-tab-mobile-${city.slug}`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeCityChip"
                      className="absolute inset-0 bg-amber-50 rounded-full z-[-1]"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  {city.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-96 rounded-2xl shimmer border border-slate-200" />
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="text-center py-12 rounded-2xl border border-destructive/20 bg-destructive/5" role="alert">
            <p className="text-sm font-bold text-slate-900">Trending properties could not be loaded.</p>
            <p className="mt-1 text-xs text-destructive font-medium">{error}</p>
            <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => setReloadKey((value) => value + 1)}>Try again</Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && properties.length === 0 && (
          <div className="text-center py-12 rounded-2xl border border-dashed bg-card/50">
            <p className="text-xs text-muted-foreground">
              No trending listings found for {activeCity} at this moment.
            </p>
          </div>
        )}

        {/* Grid and swipeable carousel for smaller viewports */}
        {!loading && !error && properties.length > 0 && (
          <div className="flex gap-6 overflow-x-auto scrollbar-none snap-x snap-mandatory flex-nowrap md:grid md:grid-cols-3 lg:grid-cols-4 lg:overflow-visible pb-4 md:pb-0 -mx-6 px-6 md:mx-0 md:px-0">
            {properties.map((p) => (
              <div 
                key={p.property_id || p.id} 
                className="snap-center shrink-0 w-[85%] sm:w-[48%] md:w-auto"
              >
                <PropertyCard property={p} />
              </div>
            ))}
          </div>
        )}

        {/* Right Aligned Dynamic Link at bottom */}
        {properties.length > 0 && (
          <div className="mt-8 flex justify-end">
            <Link 
              to={`/properties?city=${encodeURIComponent(activeCity)}&sort=trending`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 hover:text-amber-800 transition"
              data-testid="trending-view-all-link"
            >
              View All in {activeCity} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
